<template>
  <div class="padding-top padding-right">
    <c-page-section v-if="hasEditPermission()" squares class="q-mb-lg">
      <q-toolbar v-if="hasEditPermission()" ref="pageToolbarRef" class="page-toolbar" :class="{ column: isMobile }">
        <div
          :class="{
            'col-xs-3 col-md-3 filter-dropdowns-wrapper': !isMobile,
            'full-width filter-dropdowns-wrapper': isMobile,
          }"
        >
          <div class="page-toolbar-dropdown">
            <user-dropdown
              v-model:selectedItemValue="filter.userUuid"
              data-testid="user-filter-dropdown"
              :items="usersDropdown"
              :label="t('user1on1s.user')"
            />
          </div>
        </div>
        <q-separator vertical />
        <div
          :class="{
            'full-height col-xs-5 col-md-3 col-lg-3': !isMobile,
            'full-width': isMobile,
          }"
        >
          <q-checkbox
            v-model="filter.isFinancialMessage"
            data-testid="only-financial-messages-checkbox"
            class="only-financial-messages-checkbox"
            name="only-financial-messages-checkbox"
            :label="t('user1on1s.onlyFinanceNotes')"
          />
        </div>
        <q-separator vertical />
        <q-space />
        <div
          :class="{
            'col-xs-3 col-md-2': !isMobile,
            'full-width': isMobile,
          }"
        >
          <div>
            <div class="page-toolbar-action">
              <toolbar-add-button
                data-testid="add-1on1"
                :label="t('user1on1s.add1on1')"
                :disabled="authStore.currentUser?.uuid === filter.userUuid"
                @click="openAddDialog"
              />
            </div>
          </div>
        </div>
      </q-toolbar>
    </c-page-section>

    <div>
      <one-on-one-record
        v-for="item of user1on1sModule.user1on1s"
        :key="item.uuid"
        :data="item"
        class="q-pb-md"
        :allow-delete="filter.userUuid !== authStore.currentUser?.uuid"
        :allow-edit="filter.userUuid !== authStore.currentUser?.uuid"
        @click:delete="onDelete1on1Click"
        @click:edit="onEdit1on1Click"
      />
    </div>
    <c-alert
      class="q-mt-lg"
      type="info"
      translate-message="common.noTableData"
      :show="user1on1sModule.user1on1s.length <= 0"
      hide-close-button
    />
    <one-on-one-dialog ref="oneOnOneDialog" :has-user-vision="Boolean(selectedUserVision)" @saved="onSaved" />
    <c-confirm-dialog ref="confirmDialog" />
  </div>
</template>

<script lang="ts" setup>
import CPageSection from '@/components/_common/PageSection.vue';
import { useI18n } from 'vue-i18n';
import { computed, ref, watch } from 'vue';
import { UserDropdownItem } from '@/components/_common/dropdown/DropdownItem';
import userModule from '@/store/modules/UserModule';
import user1on1sModule from '@/store/modules/User1on1sModule';
import OneOnOneRecord from '@/components/user1on1s/OneOnOneRecord.vue';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import OneOnOneDialog from '@/components/user1on1s/OneOnOneDialog.vue';
import { useUser1on1 } from '@/composables/useUser1on1';
import { hubClient } from '@tfpl/api-client';
import CAlert from '@/components/_common/CommonAlert.vue';
import CConfirmDialog from '@/components/_common/confirmDialog/ConfirmDialog.vue';
import UserDropdown from '@/components/_common/dropdown/UserDropdown.vue';
import ToolbarAddButton from '@/components/_common/ToolbarAddButton.vue';
import { useWindowDimensions } from '@/composables/useWindowDimensions';
import { bp720 } from '@/constants/breakpoints';
import { useAuthStore } from '@/store/auth';
import { useAuthorization } from '@/composables/auth/useAuthorization';
import { permissions } from '@/store/modules/PositionModule';
import UserVisionModule from '@/store/modules/UserVisionModule';

const { t } = useI18n();
const authStore = useAuthStore();
const { hasLoggedUserPermission } = useAuthorization();

const windowDimensions = useWindowDimensions();
const isMobile = computed(() => windowDimensions.width < bp720);

/**
 * User Vision
 */
const selectedUserVision = computed(() => UserVisionModule.userVision);

/**
 * Permissions
 */
const hasEditPermission = () => {
  return (
    hasLoggedUserPermission(permissions.HUB_USERS_1ON1.MANAGE_ALL) ||
    hasLoggedUserPermission(permissions.HUB_USERS_1ON1.MANAGE_TEAM_MEMBER)
  );
};

/**
 * Filter
 */
const filter = ref<{
  userUuid?: string;
  isFinancialMessage: boolean;
}>({
  userUuid: authStore.currentUser ? authStore.currentUser.uuid : undefined,
  isFinancialMessage: false,
});

/**
 * Users
 */
if (
  hasLoggedUserPermission(permissions.HUB_USERS_1ON1.MANAGE_ALL) ||
  hasLoggedUserPermission(permissions.HUB_USERS_1ON1.MANAGE_TEAM_MEMBER)
) {
  userModule.fetchUsers(permissions.HUB_USERS_1ON1.READ_OWN);
}
const usersDropdown = computed((): UserDropdownItem[] =>
  userModule.users
    .filter((u) => u.isActive)
    .filter((u) => {
      if (hasLoggedUserPermission(permissions.HUB_USERS_1ON1.MANAGE_ALL)) {
        return true;
      } else if (hasLoggedUserPermission(permissions.HUB_USERS_1ON1.MANAGE_TEAM_MEMBER)) {
        if (!u.responsiblePerson) {
          return false;
        }
        if (u.responsiblePerson.uuid === authStore.currentUser?.uuid) {
          return true;
        }
        if (u.uuid === authStore.currentUser?.uuid) {
          return true;
        }
        return false;
      } else {
        return false;
      }
    })
    .map((user) => ({
      name: user.fullName,
      value: user.uuid,
      avatarUrl: user.slackAvatarUrl,
    })),
);

/**
 * User 1on1s
 */
watch(
  filter,
  (newFilter) => {
    if (user1on1sModule.onlyFinancialMessages != newFilter.isFinancialMessage) {
      user1on1sModule.setOnlyFinancialMessages(newFilter.isFinancialMessage);
    }

    if (hasEditPermission()) {
      if (newFilter.userUuid) {
        user1on1sModule.fetchUser1on1s({
          collaboratorUuid: newFilter.userUuid,
        });
        // Fetch user vision for AI validation
        UserVisionModule.fetchUserVision(newFilter.userUuid);
      } else {
        user1on1sModule.clearData();
      }
    } else {
      user1on1sModule.fetchMy1on1s();
    }
  },
  { deep: true, immediate: true },
);

/**
 * Add / edit dialog
 */
const oneOnOneDialog = ref();

const openAddDialog = () => {
  if (filter.value.userUuid) {
    oneOnOneDialog.value.openDialog(filter.value.userUuid);
  }
};

const onSaved = () => {
  if (filter.value.userUuid) {
    user1on1sModule.fetchUser1on1s({
      collaboratorUuid: filter.value.userUuid,
    });
  }
};

const onEdit1on1Click = (oneOnOne: hubClient.Collaborator1On1Dto) => {
  if (filter.value.userUuid) {
    oneOnOneDialog.value.openDialog(filter.value.userUuid, oneOnOne);
  }
};

/**
 * Delete dialog
 */
const confirmDialog = ref();
const { delete1on1 } = useUser1on1();
const onDelete1on1Click = (uuid: string) => {
  confirmDialog.value.showDialog({
    title: t('user1on1s.deleteConfirm'),
    cancelBtnLabel: t('cancelBtn'),
    confirmBtnLabel: t('delete'),
    confirmAction: async () => {
      await delete1on1(uuid);
      await user1on1sModule.fetchUser1on1s({
        collaboratorUuid: filter.value.userUuid as string,
      });
      confirmDialog.value.closeDialog();
    },
  });
};
</script>
<style lang="scss" scoped>
@import 'styles/app.variables.scss';
.page-toolbar-action {
  width: auto;
}
.page-toolbar-dropdown {
  max-width: none;
}
</style>
